import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { DATE_KEY_FORMAT } from '@/helpers';

import {
  ASSIGNMENT_BAR_HEIGHT_REM,
  ASSIGNMENT_BASE_OFFSET_REM,
  ASSIGNMENT_LEVEL_SPACING_REM,
  REM_TO_PX,
} from '../constants';
import { AssignmentType, TimeOffBlockType } from '../types';
import {
  DayPortionDTO,
  TimeOffRequestStatusDTO,
} from '../types/dto/response/planner-timeoff-response';
import { getDayLayout } from './time-off-layout';

dayjs.extend(isSameOrBefore);

const ASSIGNMENT_BAR_HEIGHT_PX = ASSIGNMENT_BAR_HEIGHT_REM * REM_TO_PX;
// Vertical gap between stacked bars when they share a time-off free zone. Tighter
// than the full level spacing so more bars fit before falling back to over-block.
const FREE_ZONE_STACK_GAP_PX = 4;

/**
 * Effective minutes-per-day to DISPLAY for a rendered fragment that spans
 * `startDate..endDate`. When a partial (e.g. half-day) time-off overlaps the
 * assignment, the backend sends `adjustedMinutesByDate` (e.g. `{ "2026-06-09": 240 }`)
 * — the remaining capacity that day. `splitFragmentsAroundTimeOffs` already isolates
 * such days into their own fragment, so a fragment is either entirely "normal" or
 * entirely "adjusted". When every day in the span carries the SAME adjusted value, we
 * show that instead of the assignment's full `minutesPerDay` (fixes the planner showing
 * 8h on a day a half-day time-off leaves only 4h). Otherwise the full value is shown.
 */
export function getFragmentDisplayMinutes(
  {
    fullMinutes,
    adjustedMinutesByDate,
  }: { fullMinutes: number; adjustedMinutesByDate?: Record<string, number> },
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): number {
  const adjusted = adjustedMinutesByDate;
  if (!adjusted) return fullMinutes;

  let uniformAdjusted: number | null = null;
  for (let d = startDate.startOf('day'); d.isSameOrBefore(endDate, 'day'); d = d.add(1, 'day')) {
    const value = adjusted[d.format(DATE_KEY_FORMAT)];
    // Any day without an adjustment (or a differing one) → not a uniform reduced
    // fragment; fall back to the assignment's full minutes.
    if (value === undefined) return fullMinutes;
    if (uniformAdjusted === null) uniformAdjusted = value;
    else if (uniformAdjusted !== value) return fullMinutes;
  }

  return uniformAdjusted ?? fullMinutes;
}

export interface RenderableFragment {
  key: string;
  assignment: AssignmentType;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
  top: string;
}

export interface AssignmentFragment {
  fragmentKey: string;
  assignment: AssignmentType;
  periodIndex: number | null;
}

interface TimeOffDayPlacement {
  portion: DayPortionDTO;
  dayPartNumber: number | null;
  status: TimeOffRequestStatusDTO;
  /** Minutes of time-off this day — used to detect full-capacity coverage
   *  (an approved HOURLY day equal to the day's capacity is a full cut). */
  minutes: number;
}

/** A HALF/QUARTER time-off leaves a free zone the assignment can move into. */
function isPartialPortion(portion: DayPortionDTO): boolean {
  return portion === DayPortionDTO.HALF || portion === DayPortionDTO.QUARTER;
}

/**
 * Builds a per-date lookup of the time-off day that drives assignment placement.
 *
 * Includes BOTH approved and pending days, because both can affect where the
 * assignment sits:
 * - APPROVED: the backend cuts the assignment (`adjustedMinutesByDate`) — it is
 *   hidden on full-cut days and repositioned into the free zone on partial days.
 * - PENDING: the backend does NOT cut, so the assignment is never hidden, but a
 *   partial (HALF/QUARTER) pending time-off still pushes the assignment into the
 *   free zone (below/above the time-off) instead of overlapping it on the top
 *   line. A FULL/HOURLY pending time-off keeps the assignment at its normal
 *   full-height level (drawn over the block — there is no free zone to use).
 *
 * When a date has multiple time-offs, an APPROVED one wins over PENDING (the cut
 * is authoritative); otherwise the last one seen wins.
 */
export function buildTimeOffPlacementLookup(
  timeOffs: TimeOffBlockType[]
): Map<string, TimeOffDayPlacement> {
  const lookup = new Map<string, TimeOffDayPlacement>();
  for (const timeOff of timeOffs) {
    for (const day of timeOff.originalTimeOff.days) {
      const dateKey = dayjs(day.date).format(DATE_KEY_FORMAT);
      const existing = lookup.get(dateKey);
      // Approved wins over pending; don't let a pending entry overwrite approved.
      if (
        existing?.status === TimeOffRequestStatusDTO.APPROVED &&
        timeOff.status !== TimeOffRequestStatusDTO.APPROVED
      ) {
        continue;
      }
      lookup.set(dateKey, {
        portion: day.portion,
        dayPartNumber: day.dayPartNumber,
        status: timeOff.status,
        minutes: day.minutes,
      });
    }
  }
  return lookup;
}

/**
 * True when an APPROVED time-off consumes the ENTIRE day's capacity, leaving no
 * room for any assignment: a FULL-portion day, or an HOURLY day whose minutes
 * meet/exceed the day's capacity (e.g. "08:00 / day" on an 8h day). Such days are
 * full cuts even when the backend omits the assignment from `adjustedMinutesByDate`.
 *
 * `getCapacityMinutes` resolves the day's working capacity (minutes); when it's
 * unknown (≤ 0) we can't compare, so an HOURLY day is treated as non-full.
 */
function isApprovedFullCapacityDay(
  placement: TimeOffDayPlacement,
  capacityMinutes: number
): boolean {
  if (placement.status !== TimeOffRequestStatusDTO.APPROVED) return false;
  if (placement.portion === DayPortionDTO.FULL) return true;
  if (placement.portion === DayPortionDTO.HOURLY && capacityMinutes > 0) {
    return placement.minutes >= capacityMinutes;
  }
  return false;
}

/**
 * True when `dateKey` is fully cut out of `assignment` by an APPROVED time-off:
 * the backend reduced the assignment's minutes to 0, OR the time-off covers the
 * whole day's capacity (FULL, or HOURLY ≥ capacity) even when the backend omits
 * it from `adjustedMinutesByDate`. Pending time-offs never fully cut — the bar
 * stays interactive over them.
 *
 * Must mirror the hide rule in {@link splitFragmentsAroundTimeOffs} so the cell
 * is treated as empty exactly where the assignment fragment is hidden.
 *
 * `placementLookup` is the map from {@link buildTimeOffPlacementLookup}, built
 * once per row and shared across assignments. `getCapacityMinutes` resolves a
 * date's working capacity for the HOURLY-full check.
 */
export function isAssignmentDateCutByApprovedTimeOff(
  assignment: AssignmentType,
  dateKey: string,
  placementLookup: Map<string, TimeOffDayPlacement>,
  getCapacityMinutes?: (dateKey: string) => number
): boolean {
  const placement = placementLookup.get(dateKey);
  if (placement?.status !== TimeOffRequestStatusDTO.APPROVED) return false;
  const adjusted = assignment.adjustedMinutesByDate?.[dateKey];
  if (adjusted === 0) return true;
  // Full-capacity approved day (FULL, or HOURLY ≥ capacity) → fully cut, mirroring
  // the hide rule in splitFragmentsAroundTimeOffs.
  const capacity = getCapacityMinutes?.(dateKey) ?? 0;
  return isApprovedFullCapacityDay(placement, capacity);
}

function getLevelTop(level: number): string {
  return `${level * ASSIGNMENT_LEVEL_SPACING_REM + ASSIGNMENT_BASE_OFFSET_REM}rem`;
}

function getFragmentDates(fragment: AssignmentFragment): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  if (fragment.periodIndex === null) {
    return { start: fragment.assignment.startDate, end: fragment.assignment.endDate };
  }
  const period = fragment.assignment.activePeriods[fragment.periodIndex];
  return { start: period.startDate, end: period.endDate };
}

/** Per-day classification of how a time-off affects an assignment that day. */
type DayEffect =
  | { kind: 'cut' } // approved full-cut → assignment hidden (gap)
  | { kind: 'partial'; portion: DayPortionDTO; dayPartNumber: number | null } // HALF/QUARTER free zone
  | { kind: 'normal' }; // no time-off, hourly partial, pending full-day → normal level

/**
 * Classifies how the time-off on `dateKey` affects this assignment that day.
 * Only HALF/QUARTER days yield a usable free zone; HOURLY (and pending full-day)
 * leave no free assignment zone, so the bar stays at its normal level over the
 * block. An approved full-capacity day (FULL, or HOURLY ≥ capacity) is a cut.
 */
function classifyDay(
  assignment: AssignmentType,
  dateKey: string,
  placement: TimeOffDayPlacement | undefined,
  originalMinutes: number,
  capacityMinutes: number
): DayEffect {
  if (!placement) return { kind: 'normal' };

  const isApproved = placement.status === TimeOffRequestStatusDTO.APPROVED;
  const adjusted = isApproved ? assignment.adjustedMinutesByDate?.[dateKey] : undefined;

  // Approved full-cut — assignment hidden this day. Either the backend zeroed
  // `adjustedMinutesByDate`, OR the approved time-off covers the whole day's
  // capacity (FULL, or HOURLY ≥ capacity). A full-day approved time-off leaves no
  // room for any assignment, so it is always hidden — even for background
  // assignments the backend omits from `adjustedMinutesByDate`.
  if (adjusted === 0 || isApprovedFullCapacityDay(placement, capacityMinutes)) {
    return { kind: 'cut' };
  }

  // A HALF/QUARTER day leaves a free zone the bar can move into: approved with
  // reduced (non-zero) minutes, or pending (backend doesn't cut pending).
  const isApprovedPartial = isApproved && adjusted !== undefined && adjusted < originalMinutes;
  const isPendingPartial =
    placement.status === TimeOffRequestStatusDTO.PENDING && isPartialPortion(placement.portion);
  if ((isApprovedPartial || isPendingPartial) && isPartialPortion(placement.portion)) {
    return { kind: 'partial', portion: placement.portion, dayPartNumber: placement.dayPartNumber };
  }

  return { kind: 'normal' };
}

/**
 * Resolves the free-zone top (px) for ONE partial day, stacking the bar by its
 * level. Returns null when the bar must stay at its normal level (the level stack
 * doesn't fit the day's free zone) — the caller then draws it over the block.
 *
 * `dayMaxLevel` is the highest assignment level PRESENT ON THIS DAY (not the whole
 * row), so a day with a single assignment isn't forced over the block just because
 * other days elsewhere stack more bars. Each level's bar sits at
 * `stackTop + level × (barHeight + gap)`, the whole stack centered in the free
 * zone. Fits only if the full local stack fits — otherwise every level falls back
 * to its normal position so the bars never collide inside a too-small zone.
 */
function resolveFreeZoneTop(
  level: number,
  dayMaxLevel: number,
  portion: DayPortionDTO,
  dayPartNumber: number | null,
  cellHeight: number
): string | null {
  if (cellHeight <= 0) return null;

  const layout = getDayLayout(cellHeight, portion, dayPartNumber);
  const zoneTop = layout.assignment.topPx;
  const zoneHeight = layout.assignment.heightPx;

  const stackCount = dayMaxLevel + 1;
  const stackHeight =
    stackCount * ASSIGNMENT_BAR_HEIGHT_PX + (stackCount - 1) * FREE_ZONE_STACK_GAP_PX;

  // Whole stack must fit the free zone; otherwise fall back to over-block so the
  // bars keep their normal (well-separated) level positions and never collide.
  if (stackHeight > zoneHeight) return null;

  // Center the stack in the free zone, then offset this bar by its level.
  const stackTop = zoneTop + Math.max(0, (zoneHeight - stackHeight) / 2);
  const top = stackTop + level * (ASSIGNMENT_BAR_HEIGHT_PX + FREE_ZONE_STACK_GAP_PX);
  return `${top}px`;
}

/**
 * Builds a per-date map of the highest assignment level present that day, from
 * every fragment's date span. Used so the free-zone fit check counts only the
 * bars actually sharing a given day, not the row-wide maximum.
 */
function buildDayMaxLevel(
  fragments: AssignmentFragment[],
  assignmentLevels: Map<number, number>
): Map<string, number> {
  const dayMaxLevel = new Map<string, number>();
  for (const fragment of fragments) {
    const level = assignmentLevels.get(fragment.assignment.originalAssignment.id) ?? 0;
    const { start, end } = getFragmentDates(fragment);
    for (let d = start; d.isSameOrBefore(end, 'day'); d = d.add(1, 'day')) {
      const dateKey = d.format(DATE_KEY_FORMAT);
      dayMaxLevel.set(dateKey, Math.max(dayMaxLevel.get(dateKey) ?? 0, level));
    }
  }
  return dayMaxLevel;
}

/**
 * Splits assignment fragments around time-off days, positioning EACH DAY in the
 * empty part the time-off leaves free:
 *
 * - APPROVED full-cut (adjustedMinutesByDate === 0, or an approved full-capacity
 *   day — FULL, or HOURLY ≥ capacity): the assignment is hidden that day (a gap).
 * - HALF/QUARTER partial day (approved-reduced or pending): the bar drops into
 *   that day's free zone (below a top-aligned block, above a bottom-aligned one),
 *   stacked by level so multiple assignments never overlap. If the level stack
 *   can't fit the free zone, the bar stays at its normal level over the block.
 * - Normal day (no time-off, hourly partial, pending full-day): normal level.
 *
 * Consecutive days that resolve to the SAME top merge into one fragment; days
 * with different tops split into separate fragments. So a uniform partial span
 * is one repositioned bar, while a span with varying time-offs splits per-day
 * and each day sits in its own empty part (instead of one bar over everything).
 *
 * `cellHeight` is the ACTUAL row height — free zones are computed against it, so
 * bars always sit within the row (which only grows from assignment levels, never
 * from time-off). `getCapacityMinutes` resolves a date's capacity for the
 * HOURLY-full-cut check.
 */
export function splitFragmentsAroundTimeOffs(
  fragments: AssignmentFragment[],
  assignmentLevels: Map<number, number>,
  timeOffs: TimeOffBlockType[],
  cellHeight: number = 0,
  getCapacityMinutes?: (dateKey: string) => number
): RenderableFragment[] {
  const placementLookup = buildTimeOffPlacementLookup(timeOffs);
  const result: RenderableFragment[] = [];

  // Per-day highest stacking level — drives the free-zone fit check so only the
  // bars actually sharing a given day count (a day with one assignment isn't
  // forced over the block just because the row stacks more bars elsewhere).
  const dayMaxLevel = buildDayMaxLevel(fragments, assignmentLevels);

  for (const fragment of fragments) {
    const assignmentId = fragment.assignment.originalAssignment.id;
    const level = assignmentLevels.get(assignmentId) ?? 0;
    const { start, end } = getFragmentDates(fragment);
    const normalTop = getLevelTop(level);

    if (placementLookup.size === 0) {
      result.push({
        key: fragment.fragmentKey,
        assignment: fragment.assignment,
        startDate: start,
        endDate: end,
        top: normalTop,
      });
      continue;
    }

    const originalMinutes = fragment.assignment.originalAssignment.minutesPerDay;

    // Build per-day fragments, merging consecutive days with the same resolved
    // top and breaking the run on full-cut (hidden) days.
    let groupStart: dayjs.Dayjs | null = null;
    let groupEnd: dayjs.Dayjs | null = null;
    let groupTop: string | null = null;
    let subIndex = 0;

    const flushGroup = () => {
      if (groupStart && groupEnd && groupTop !== null) {
        result.push({
          key: `${fragment.fragmentKey}-s${subIndex++}`,
          assignment: fragment.assignment,
          startDate: groupStart,
          endDate: groupEnd,
          top: groupTop,
        });
      }
      groupStart = null;
      groupEnd = null;
      groupTop = null;
    };

    for (let d = start; d.isSameOrBefore(end, 'day'); d = d.add(1, 'day')) {
      const dateKey = d.format(DATE_KEY_FORMAT);
      const placement = placementLookup.get(dateKey);
      const capacity = getCapacityMinutes?.(dateKey) ?? 0;
      const effect = classifyDay(
        fragment.assignment,
        dateKey,
        placement,
        originalMinutes,
        capacity
      );

      if (effect.kind === 'cut') {
        flushGroup();
        continue;
      }

      // Resolve this day's top: a partial day drops into its free zone (stacked
      // by level), unless the stack can't fit — then it stays at the normal level
      // over the block. Normal days keep the normal level.
      let dayTop = normalTop;
      if (effect.kind === 'partial') {
        const freeZoneTop = resolveFreeZoneTop(
          level,
          dayMaxLevel.get(dateKey) ?? level,
          effect.portion,
          effect.dayPartNumber,
          cellHeight
        );
        if (freeZoneTop !== null) dayTop = freeZoneTop;
      }

      // Extend the current run when the top matches; otherwise flush and restart.
      if (groupStart && groupTop === dayTop) {
        groupEnd = d;
      } else {
        flushGroup();
        groupStart = d;
        groupEnd = d;
        groupTop = dayTop;
      }
    }
    flushGroup();
  }

  return result;
}
